// src/components/settings/catalog/CatalogImport.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 6 (screen): IMPORT.
//
// Import a price list (the approved mockup, Import.dc.html): 1 Choose file → 2 Check the changes →
// 3 Import. The browser reads the CSV (papaparse, every line kept so row numbers match the file)
// and sends its cells to planCatalogImport — the file is never stored. The SERVER decides
// everything: how each row matches, what it may change, which rows need fixing, the >20% tick and
// CHANGE (confirmCatalogImport enforces both; its refusal is shown as it says it). The import runs
// through the same job runner as bulk changes (CatalogJobRunner), and can be undone from Import history.
import React, { useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import { Loader2, Upload, FileText, Download, History, Check } from 'lucide-react';
import {
    IMPORT_MATCH_LINE, IMPORT_EXPIRED, importTemplateCsv, importFileProblem, prepareImportRows, filterImportRows,
    importChips, importBadge, describeImportRow, importGate, isImportExpiredError, LARGE_CHANGE,
} from '../../../utils/catalogUi';
import { planCatalogImport, confirmCatalogImport, downloadCsv, friendlyError } from './catalogApi';
import { BTN_GHOST, BTN_PRIMARY, BTN_LINK, ErrorLine, TypedConfirm } from './catalogUiParts';

const PAGE = 200;
const fmt = (n) => (n || 0).toLocaleString('en-US');
const BADGE = {
    update: 'bg-blue-50 text-blue-800',
    new: 'bg-emerald-50 text-emerald-800',
    fix: 'bg-orange-50 text-orange-800',
    big: 'bg-orange-50 text-orange-800 border border-orange-200 font-bold',
    same: 'bg-gray-100 text-gray-600',
};
const CHECK_ERROR = "Couldn't check the file. Try again.";

// A progress indicator, NOT buttons (Marcus mistook the old filled pills for the Import button,
// 2026-10-25): plain text, no fill, no border, no pointer — "Step 2 of 3: Check the changes" and a
// quiet list with checkmarks for the steps already done.
const STEP_LABELS = ['Choose file', 'Check the changes', 'Import'];
function Steps({ step }) {
    return (
        <nav aria-label="Import progress" className="space-y-1 cursor-default select-none">
            <p className="text-sm font-semibold text-gray-800">Step {step} of 3: {STEP_LABELS[step - 1]}</p>
            <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-gray-500">
                {STEP_LABELS.map((label, i) => {
                    const n = i + 1;
                    return (
                        <li key={label} className="flex items-center gap-1.5" aria-current={n === step ? 'step' : undefined}>
                            {n > 1 && <span aria-hidden="true" className="text-gray-300">—</span>}
                            {n < step
                                ? <Check size={13} className="text-emerald-600" aria-hidden="true" />
                                : <span aria-hidden="true" className={n === step ? 'text-gray-800 font-semibold' : ''}>{n}.</span>}
                            <span className={n === step ? 'text-gray-800 font-semibold' : ''}>{label}</span>
                            {n < step && <span className="sr-only">(done)</span>}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

function Card({ value, label, warn }) {
    return (
        <div className={`rounded-lg p-3 ${warn ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'}`}>
            <div className={`text-[22px] font-bold ${warn ? 'text-orange-800' : 'text-gray-900'}`}>{fmt(value)}</div>
            <div className={`text-[13px] ${warn ? 'text-orange-800' : 'text-gray-600'}`}>{label}</div>
        </div>
    );
}

function Badge({ row }) {
    const b = importBadge(row);
    return <span className={`inline-block whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold ${BADGE[b.tone]}`}>{b.label}</span>;
}

export default function CatalogImport({ runner, onBack, onHistory }) {
    const fileInput = useRef(null);
    const tableTop = useRef(null);
    const [phase, setPhase] = useState('choose'); // choose | reading | checking | preview
    const [file, setFile] = useState(null); // { name, rowCount }
    const [plan, setPlan] = useState(null);
    const [error, setError] = useState('');
    const [expired, setExpired] = useState(false);
    const [chip, setChip] = useState('all');
    const [shown, setShown] = useState(PAGE);
    const [bigOk, setBigOk] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const running = !!runner.state;
    const importing = running && runner.state.job && runner.state.job.action === 'import';

    const reset = () => {
        setPlan(null); setFile(null); setError(''); setExpired(false); setChip('all'); setShown(PAGE);
        setBigOk(false); setConfirmText(''); setSubmitError('');
    };
    const chooseAgain = () => { reset(); setPhase('choose'); if (fileInput.current) { fileInput.current.value = ''; fileInput.current.click(); } };

    const onPick = async (e) => {
        const f = e.target.files && e.target.files[0];
        if (!f) return;
        reset();
        const problem = importFileProblem(f.name, f.size);
        if (problem) { setError(problem); setPhase('choose'); return; }
        setPhase('reading');
        let prepared;
        try {
            const text = await f.text();
            // Every line kept (skipEmptyLines off) so row 7 here is row 7 in the spreadsheet.
            const parsed = Papa.parse(text, { delimiter: ',', skipEmptyLines: false });
            prepared = prepareImportRows(parsed.data);
        } catch (err) {
            console.error('Catalog import: reading the file failed', err);
            setError("Couldn't read this file. Save it as CSV and choose it again.");
            setPhase('choose');
            return;
        }
        if (prepared.problem) { setError(prepared.problem); setPhase('choose'); return; }
        setFile({ name: f.name, rowCount: prepared.rowCount });
        setPhase('checking');
        try {
            const r = await planCatalogImport({ fileName: f.name, headers: prepared.headers, rows: prepared.rows });
            setPlan(r);
            setPhase('preview');
        } catch (err) {
            console.error('Catalog import: check failed', err);
            setError(friendlyError(err, CHECK_ERROR));
            setPhase('choose');
        }
    };

    const rows = useMemo(() => (plan && Array.isArray(plan.rows) ? plan.rows : []), [plan]);
    const chips = useMemo(() => importChips(rows), [rows]);
    const filtered = useMemo(() => filterImportRows(rows, chip), [rows, chip]);
    const visible = filtered.slice(0, shown);
    const s = (plan && plan.summary) || {};
    const gate = importGate(plan, { bigOk, confirmText, nowMs: Date.now() });
    const step = importing ? 3 : (phase === 'preview' ? 2 : 1);

    const pickChip = (key) => { setChip(key); setShown(PAGE); };
    const showBig = () => { pickChip('big'); if (tableTop.current) tableTop.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

    const doImport = async () => {
        if (!gate.ok || submitting) return;
        if (Date.now() > plan.expiresAtMs) { setExpired(true); return; }
        setSubmitting(true);
        setSubmitError('');
        try {
            await confirmCatalogImport({ jobId: plan.jobId, ...(plan.needsBigTick ? { checkedBigChanges: bigOk } : {}), ...(plan.needsConfirmWord ? { confirm: confirmText } : {}) });
            runner.run({ jobId: plan.jobId, action: 'import', total: plan.toImport, processed: 0, newCount: s.new || 0, fileName: plan.fileName });
        } catch (err) {
            console.error('Catalog import: confirm refused', err);
            if (isImportExpiredError(err)) setExpired(true);
            else setSubmitError(friendlyError(err, "Couldn't start the import. Try again."));
        } finally {
            setSubmitting(false);
        }
    };

    const downloadTemplate = () => downloadCsv(importTemplateCsv(), 'catalog-import-template.csv');

    // The Import button, shown at the TOP (under the big-change tick) and at the bottom (2026-10-25):
    // one component on the same state, so both say and do exactly the same thing.
    const importButton = (where) => {
        let label = gate.ok ? `Import ${fmt(plan.toImport)} change${plan.toImport === 1 ? '' : 's'}` : gate.reason;
        if (where === 'top' && !gate.ok && gate.reason === 'Type CHANGE to confirm') label = 'Type CHANGE below to confirm';
        return (
            <button type="button" className={BTN_PRIMARY} disabled={!gate.ok || submitting || running || expired} onClick={doImport}>
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {label}
            </button>
        );
    };

    return (
        <div className="space-y-5 max-w-[1100px]">
            <button type="button" className={BTN_LINK} onClick={onBack} disabled={running}>← Back to Catalog</button>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-[22px] sm:text-[26px] font-bold text-gray-900">Import a price list</h2>
                    <p className="text-sm text-gray-500 mt-1">Nothing changes until you press Import. You'll see every change first.</p>
                </div>
                <button type="button" className={BTN_GHOST} onClick={onHistory} disabled={running}><History size={16} /> Import history</button>
            </div>
            <Steps step={step} />

            <input ref={fileInput} type="file" accept=".csv,text/csv,.txt,.xlsx,.xls" className="sr-only" tabIndex={-1} aria-hidden="true" onChange={onPick} />

            {(phase === 'choose' || phase === 'reading') && (
                <section aria-label="Choose file" className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-4">
                    <div className="space-y-1">
                        <h3 className="text-base font-semibold text-gray-900">Choose a CSV file</h3>
                        <p className="text-sm text-gray-600">Use the columns from Export or Download template. Each row is {IMPORT_MATCH_LINE}. Rows that match nothing become new items.</p>
                        <p className="text-sm text-gray-600">Blank cells are left as they are. Names are only used to find items and to name new ones — import never renames an item.</p>
                    </div>
                    <ErrorLine text={error} />
                    <div className="flex flex-wrap gap-2">
                        <button type="button" className={BTN_PRIMARY} disabled={phase === 'reading'} onClick={() => fileInput.current && fileInput.current.click()}>
                            {phase === 'reading' ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {phase === 'reading' ? 'Reading the file…' : 'Choose file'}
                        </button>
                        <button type="button" className={BTN_GHOST} onClick={downloadTemplate}><Download size={16} /> Download template</button>
                    </div>
                    <p className="text-xs text-gray-500">Excel file? Save it as CSV first (File → Save As → CSV). Up to 75,000 rows or 10 MB at a time.</p>
                </section>
            )}

            {(phase === 'checking' || phase === 'preview') && file && (
                <section aria-label="File" className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex items-start gap-2">
                        <FileText size={18} className="text-gray-400 mt-0.5 shrink-0" aria-hidden="true" />
                        <div className="min-w-0">
                            <div className="text-[15px] font-bold text-gray-900 break-all">{file.name}</div>
                            <div className="text-[13px] text-gray-500">{fmt(file.rowCount)} row{file.rowCount === 1 ? '' : 's'} · {IMPORT_MATCH_LINE}</div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" className={BTN_GHOST} disabled={phase === 'checking' || running || submitting} onClick={chooseAgain}>Choose a different file</button>
                        <button type="button" className={BTN_GHOST} onClick={downloadTemplate}>Download template</button>
                    </div>
                </section>
            )}

            {phase === 'checking' && (
                <p className="text-sm text-gray-700 flex items-center gap-2" aria-live="polite">
                    <Loader2 size={16} className="animate-spin text-[#5F4EC9]" aria-hidden="true" />
                    Checking {fmt(file && file.rowCount)} rows against your catalog… A very big file can take a minute.
                </p>
            )}

            {phase === 'preview' && plan && (
                <>
                    {expired && (
                        <div role="alert" className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-900">
                            <span className="flex-1 min-w-[12rem]">{IMPORT_EXPIRED}</span>
                            <button type="button" className={BTN_PRIMARY} onClick={chooseAgain}>Choose the file again</button>
                        </div>
                    )}

                    <section aria-label="Summary" className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
                        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                            <Card value={s.costsUp} label="costs up" />
                            <Card value={s.costsDown} label="costs down" />
                            <Card value={s.pricesMovedByMarkup} label="prices move (Keep my markup)" />
                            <Card value={s.big} label="changed by more than 20%" warn={s.big > 0} />
                            <Card value={s.new} label="new items" />
                            <Card value={s.unchanged} label="unchanged" />
                        </div>
                        {plan.needsBigTick && (
                            <div className="flex items-start gap-2.5 bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-900">
                                <input id="imp-big-ok" type="checkbox" className="w-5 h-5 mt-0.5 shrink-0 accent-orange-600" checked={bigOk} disabled={expired} onChange={(e) => setBigOk(e.target.checked)} />
                                <span>
                                    <label htmlFor="imp-big-ok" className="cursor-pointer">I've checked the {fmt(s.big)} big change{s.big === 1 ? '' : 's'}.</label>{' '}
                                    <button type="button" className="inline-flex items-center min-h-[44px] -my-3 font-bold underline text-orange-800" onClick={showBig}>Show them</button>
                                </span>
                            </div>
                        )}
                        {/* The real Import button, up here too, so nobody has to scroll past the rows. */}
                        <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                            {!plan.jobId && <span className="text-sm text-gray-600">Nothing to import: every row is unchanged or needs fixing.</span>}
                            {importButton('top')}
                        </div>
                        <ErrorLine text={submitError} />
                    </section>

                    <div ref={tableTop} role="group" aria-label="Show rows" className="flex flex-wrap gap-2">
                        {chips.map((c) => (
                            <button key={c.key} type="button" aria-pressed={chip === c.key} onClick={() => pickChip(c.key)}
                                className={`min-h-[44px] px-3.5 rounded-full text-[13px] font-semibold border focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 ${
                                    chip === c.key ? 'bg-[#5F4EC9] text-white border-[#5F4EC9]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                                {c.label}
                            </button>
                        ))}
                    </div>

                    <section aria-label="Changes" className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-4 space-y-3">
                        {!filtered.length && <p className="text-sm text-gray-600 p-2">No rows here.</p>}
                        {!!filtered.length && (
                            <>
                                {/* Wide screens: a table that scrolls in its own box. */}
                                <div className="hidden sm:block overflow-x-auto border border-gray-100 rounded-lg">
                                    <table className="w-full min-w-[640px] border-collapse text-sm">
                                        <thead>
                                            <tr className="bg-gray-50 text-left text-gray-600 text-xs uppercase tracking-wide">
                                                <th scope="col" className="px-3 py-2.5 font-semibold">Row</th>
                                                <th scope="col" className="px-3 py-2.5 font-semibold">Item</th>
                                                <th scope="col" className="px-3 py-2.5 font-semibold">What will change</th>
                                                <th scope="col" className="px-3 py-2.5 font-semibold">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visible.map((r) => {
                                                const d = describeImportRow(r);
                                                return (
                                                    <tr key={r.row} className="border-t border-gray-100 align-top">
                                                        <td className="px-3 py-3 text-gray-500 tabular-nums">{r.row}</td>
                                                        <td className="px-3 py-3 font-semibold text-gray-900">
                                                            {r.name || '(no name)'}
                                                            {r.archived && <span className="ml-1 text-xs font-normal text-gray-500">(archived)</span>}
                                                            {d.nameNote && <div className="text-xs font-normal text-gray-500 mt-0.5">{d.nameNote}{r.nameInFile ? ` File says “${r.nameInFile}”.` : ''}</div>}
                                                        </td>
                                                        <td className="px-3 py-3 text-gray-700 tabular-nums">
                                                            {d.text}
                                                            {d.notes.map((n) => <div key={n} className="text-xs text-gray-500 mt-0.5">{n}</div>)}
                                                        </td>
                                                        <td className="px-3 py-3"><Badge row={r} /></td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Phones: one card per row. */}
                                <ul className="sm:hidden space-y-2">
                                    {visible.map((r) => {
                                        const d = describeImportRow(r);
                                        return (
                                            <li key={r.row} className="border border-gray-200 rounded-lg p-3 space-y-1">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <div className="text-xs text-gray-500">Row {r.row}</div>
                                                        <div className="font-semibold text-gray-900 break-words">{r.name || '(no name)'}{r.archived ? ' (archived)' : ''}</div>
                                                    </div>
                                                    <Badge row={r} />
                                                </div>
                                                {d.nameNote && <div className="text-xs text-gray-500">{d.nameNote}{r.nameInFile ? ` File says “${r.nameInFile}”.` : ''}</div>}
                                                <div className="text-sm text-gray-700">{d.text}</div>
                                                {d.notes.map((n) => <div key={n} className="text-xs text-gray-500">{n}</div>)}
                                            </li>
                                        );
                                    })}
                                </ul>
                                {filtered.length > visible.length && (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm text-gray-600">Showing {fmt(visible.length)} of {fmt(filtered.length)}.</span>
                                        <button type="button" className={BTN_GHOST} onClick={() => setShown((n) => n + PAGE)}>Show {fmt(Math.min(PAGE, filtered.length - visible.length))} more</button>
                                    </div>
                                )}
                            </>
                        )}
                        <p className="text-[13px] text-gray-500">Items that aren't in the file are left alone. Import never archives or deletes anything. Rows that need fixing are skipped; fix them in the file and import again.</p>
                    </section>

                    {plan.needsConfirmWord && !expired && (
                        <TypedConfirm id="imp-confirm" line={`You're about to change ${fmt(plan.toImport)} items (more than ${fmt(LARGE_CHANGE)}).`}
                            value={confirmText} onChange={setConfirmText} />
                    )}
                    <ErrorLine text={submitError} />

                    <div className="flex flex-wrap justify-end gap-2.5">
                        <button type="button" className={BTN_GHOST} onClick={onBack} disabled={submitting || running}>Cancel</button>
                        {importButton('bottom')}
                    </div>
                    {!plan.jobId && <p className="text-sm text-gray-600 text-right">Every row is unchanged or needs fixing, so there's nothing to import.</p>}
                    <p className="text-xs text-gray-500 text-right">
                        Each price change goes into that item's history. Estimates and invoices already written don't change.<br />
                        Changed your mind? Undo the whole import from Catalog → Import history. Items edited since the import are listed and left alone.
                    </p>
                </>
            )}
        </div>
    );
}
